import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import { useRouteParams } from "@vueuse/router";
import { useMetadataExperienceApi } from "@/composables/useMetadataExperienceApi";
import { useContentCommandApi } from "@/composables/useContentCommandApi";
import { useMasterData } from "@/stores/master-data";
import { mapGenericToManageableHighlight } from "@/utils/mixed-highlights";
import type {
  MixedHighlightValue,
  HighlightsKey,
  HighlightsMasterDataKey,
  CustomHighlightsEndpoint,
  GenericHighlight,
} from "@/types/Highlights";
import type {
  ExperienceHighlights,
  ExperienceIncluded,
  ExperienceNonIncluded,
  ExperienceImportantInformation,
} from "@/types/generated/MetadataExperiencesApi";
import builder from "@rsql/builder";
import { emit } from "@rsql/emitter";

export const EXPERIENCE_HIGHLIGHTS_QUERY_KEY = "experience-highlights";

type HighlightMetadataResponse =
  | ExperienceHighlights
  | ExperienceIncluded
  | ExperienceNonIncluded
  | ExperienceImportantInformation;

const metadataEndpoints: Record<HighlightsKey, string> = {
  highlights: "experience-highlights",
  included: "experience-included",
  non_included: "experience-non-included",
  important_information: "experience-important-information",
};

const metadataFieldKeys: Record<HighlightsKey, string> = {
  highlights: "highlights",
  included: "included",
  non_included: "non_included",
  important_information: "important_information",
};

const customEndpoints: Record<HighlightsKey, CustomHighlightsEndpoint> = {
  highlights: "custom-highlights",
  included: "custom-included",
  non_included: "custom-non-included",
  important_information: "custom-important-information",
};

const masterDataStoreKeys: Record<HighlightsKey, HighlightsMasterDataKey> = {
  highlights: "highlights",
  included: "included",
  non_included: "included",
  important_information: "importantInformation",
};

export interface HighlightsQueryData {
  id: string | undefined;
  value: MixedHighlightValue;
}

export function useExperienceHighlightsQuery(experienceId: Ref<string>, fieldKey: HighlightsKey) {
  const { get } = useMetadataExperienceApi();
  const { getCustomHighlights } = useContentCommandApi();
  const masterData = useMasterData();

  return useQuery({
    queryKey: [EXPERIENCE_HIGHLIGHTS_QUERY_KEY, fieldKey, experienceId],
    queryFn: async (): Promise<HighlightsQueryData> => {
      const params = {
        filters: emit(builder.eq("experience_id", experienceId.value)),
      };

      const customFilters = emit(
        builder.and(builder.eq("language_code", "en"), builder.eq("experience_id", experienceId.value))
      );

      const [premadeResponse, customResponse] = await Promise.all([
        get<HighlightMetadataResponse[]>(metadataEndpoints[fieldKey], {
          params: { ...params, fields: metadataFieldKeys[fieldKey] },
        }),
        getCustomHighlights(customEndpoints[fieldKey], {
          params: { filters: customFilters, sort: "visualization_order" },
        }),
      ]);

      const premadeRelation = premadeResponse.data?.[0];
      const premadeCodes: string[] =
        ((premadeRelation as Record<string, unknown>)?.[metadataFieldKeys[fieldKey]] as string[]) ?? [];

      const storeKey = masterDataStoreKeys[fieldKey];
      const premade: GenericHighlight[] = premadeCodes.map((code) => {
        try {
          return masterData.getHighlightByCode(storeKey, code);
        } catch {
          return { id: code, name: code, code, action: "NOOP" as const, visualization_order: 0 };
        }
      });

      const custom = mapGenericToManageableHighlight(customResponse.data ?? []);

      return {
        id: premadeRelation?.id,
        value: { premade, custom },
      };
    },
    select: (data) => data,
    enabled: computed(() => !!experienceId.value),
  });
}

export function useCurrentExperienceHighlightsQuery(fieldKey: HighlightsKey) {
  const experienceId = useRouteParams<string>("id");
  return useExperienceHighlightsQuery(experienceId, fieldKey);
}
